'use client';

import { useCallback, useState, type ReactNode } from 'react';
import {
  HttpSendToChatProvider as DesignSystemHttpSendToChatProvider,
  type ChatMentionsMenuItemType,
  type HttpSendToChatData,
} from '@terra/ui-design-system';
import { normalizeHttpVersionNumber } from '@terra/shared-utils';
import { useChatStore } from '../../store/chatStore';
import {
  createHttpRequestMention,
  createHttpResponseMention,
} from '../../utils/httpMentionUtils';
import {
  getDefaultStatusMessage,
  truncateBodyForChat,
} from '../../utils/httpFormatter';
import {
  SelectHttpContextForChatDialog,
  type SelectHttpContextForChatDialogState,
} from '../../components/dialogs/SelectHttpContextForChatDialog';

type HttpChatRequest = NonNullable<HttpSendToChatData['request']>;
type HttpChatResponse = NonNullable<HttpSendToChatData['response']>;

const toRequestMention = ({
  id,
  request,
}: {
  id: string;
  request: HttpChatRequest;
}): ChatMentionsMenuItemType =>
  createHttpRequestMention({
    id,
    method: request.method,
    url: request.url,
    headers: request.headers,
    rawHeaders: request.rawHeaders,
    body: truncateBodyForChat(request.body),
    httpVersion: normalizeHttpVersionNumber(request.httpVersion),
  });

const toResponseMention = ({
  id,
  request,
  response,
}: {
  id: string;
  request?: HttpChatRequest;
  response: HttpChatResponse;
}): ChatMentionsMenuItemType =>
  createHttpResponseMention({
    id,
    method: request?.method ?? '',
    url: request?.url ?? '',
    statusCode: response.statusCode,
    statusText:
      response.statusText ?? getDefaultStatusMessage(response.statusCode),
    headers: response.headers,
    rawHeaders: response.rawHeaders,
    body: truncateBodyForChat(response.body),
    httpVersion: normalizeHttpVersionNumber(response.httpVersion),
  });

export function HttpSendToChatProvider({ children }: { children: ReactNode }) {
  const appendToInput = useChatStore((store) => store.appendToInput);
  const sendHttpMentionsToChat = useChatStore(
    (store) => store.sendHttpMentionsToChat,
  );
  const [contextDialogState, setContextDialogState] =
    useState<SelectHttpContextForChatDialogState | null>(null);

  const handleSendToChat = useCallback(
    ({ source, request, response }: HttpSendToChatData) => {
      const id = crypto.randomUUID();
      const requestMention = request
        ? toRequestMention({ id, request })
        : undefined;
      const responseMention = response
        ? toResponseMention({ id, request, response })
        : undefined;

      if (source === 'response' && responseMention) {
        sendHttpMentionsToChat({ mentions: [responseMention] });

        return;
      }

      if (requestMention && responseMention) {
        setContextDialogState({ requestMention, responseMention });

        return;
      }

      if (requestMention) {
        sendHttpMentionsToChat({ mentions: [requestMention] });
      }
    },
    [sendHttpMentionsToChat],
  );
  const handleCloseContextDialog = useCallback(
    () => setContextDialogState(null),
    [],
  );

  return (
    <DesignSystemHttpSendToChatProvider
      onSendToChat={handleSendToChat}
      onSendTextToChat={appendToInput}
    >
      {children}
      <SelectHttpContextForChatDialog
        state={contextDialogState}
        onClose={handleCloseContextDialog}
      />
    </DesignSystemHttpSendToChatProvider>
  );
}
